import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Database, Brain, Rocket, Code, ChevronRight, Cpu, Network, GitBranch, Terminal } from 'lucide-react';
import { FaLayerGroup, FaMicrochip, FaStream } from 'react-icons/fa';

const About = () => {
  const skills = [
    { 
      icon: <Code2 className="w-6 h-6" />, 
      title: 'Frontend', 
      description: 'HTML, CSS, React, React Native, Tailwind CSS, ElectronJS',
      gradient: "from-gray-200 to-gray-400"
    },
    { 
      icon: <Database className="w-6 h-6" />, 
      title: 'Backend', 
      description: 'Node.js, Flask, FastAPI, SQL, MongoDB, PostgreSQL',
      gradient: "from-gray-300 to-gray-500"
    },
    { 
      icon: <Brain className="w-6 h-6" />, 
      title: 'AI/ML', 
      description: 'Pandas, OpenCV, NLTK, NumPy, TensorFlow, PyTorch, LangChain',
      gradient: "from-white to-gray-300"
    },
    { 
      icon: <Rocket className="w-6 h-6" />, 
      title: 'Tools', 
      description: 'Git, Docker, AWS',
      gradient: "from-gray-200 to-gray-400"
    },
    { 
      icon: <Code className="w-6 h-6" />, 
      title: 'Skills', 
      description: 'Frontend, Backend, Fullstack, AI ML, Data Science',
      gradient: "from-gray-300 to-gray-500"
    },
    { 
      icon: <FaMicrochip className="w-6 h-6" />, 
      title: 'Deep Learning', 
      description: 'Neural Networks, Computer Vision, NLP, Reinforcement Learning',
      gradient: "from-white to-gray-300"
    }
  ];

  return (
    <motion.section 
      id="about" 
      className="py-20 bg-black relative overflow-hidden"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1 }}
    >
      {/* Animated neural network background */}
      <div className="absolute inset-0">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-white/5 rounded-full blur-xl"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 200 + 50}px`,
              height: `${Math.random() * 200 + 50}px`,
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1],
              x: [0, Math.random() * 10 - 5, 0],
              y: [0, Math.random() * 10 - 5, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Neural network grid decorative element */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:20px_20px] opacity-30" />

      <div className="max-w-6xl mx-auto px-4 relative">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-4 mb-16"
        >
          <div className="inline-block">
            <h2 className="text-5xl md:text-6xl font-bold relative">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                About Me
              </span>
              <motion.div
                className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-white to-gray-400 rounded-full"
                initial={{ width: "0%" }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
            </h2>
          </div>
          
          {/* AI/ML themed decorative elements */}
          <motion.div 
            className="flex justify-center gap-16 mt-4 text-gray-600 opacity-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 0.2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Network className="w-8 h-8" />
            <Cpu className="w-8 h-8" />
            <GitBranch className="w-8 h-8" />
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-white flex items-center gap-3">
              <FaStream className="w-6 h-6 text-gray-400" />
              My Journey
            </h3>
            <div className="space-y-6 text-lg leading-relaxed text-gray-300 relative">
              <motion.div 
                className="absolute -left-4 top-0 h-full w-1 bg-gradient-to-b from-white/80 to-white/10 rounded-full"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
              <p className="ml-2">
                I'm a passionate Full Stack Developer and AI Engineer with experience in building modern web and mobile applications
                and implementing AI solutions. Currently working on innovative projects that combine cutting-edge web
                technologies with artificial intelligence.
              </p>
              <p className="ml-2">
                My expertise lies in designing intelligent systems that leverage the power of machine learning to solve
                complex problems. I'm particularly interested in neural networks, computer vision, and natural language processing.
              </p>
              
              <motion.div
                className="p-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="absolute inset-0 opacity-10">
                  <Network className="absolute top-4 right-4 w-24 h-24 text-white opacity-10" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">My Mission</h4>
                <p className="text-gray-400">
                  To create intelligent, accessible systems that enhance human capabilities and solve real-world challenges through
                  the power of AI and innovative software engineering.
                </p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-gray-400/5 rounded-2xl blur-xl" />
            <div className="relative p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <FaLayerGroup className="w-5 h-5 text-gray-400" />
                Technical Expertise
                <motion.div
                  animate={{ 
                    rotate: [0, 360],
                  }}
                  transition={{ 
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <ChevronRight className="w-5 h-5 text-gray-400 opacity-50" />
                </motion.div>
              </h3>
              
              <div className="space-y-1 mb-8">
                {[
                  { label: "Machine Learning", value: 90 },
                  { label: "Deep Learning", value: 95 },
                  { label: "Full Stack Development", value: 95 },
                  { label: "Data Science", value: 90 },
                  
                ].map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{item.label}</span>
                      <span className="text-gray-500">{item.value}%</span>
                    </div>
                    <motion.div 
                      className="h-1 bg-gray-700 rounded-full overflow-hidden"
                      initial={{ opacity: 0.5 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <motion.div 
                        className="h-full bg-gradient-to-r from-white to-gray-500 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 + index * 0.1 }}
                      />
                    </motion.div>
                  </div>
                ))}
              </div>
              
              <h4 className="text-xl font-bold text-white mb-4">Work Areas</h4>
              <ul className="space-y-4">
                <QuickFact label="AI & Machine Learning" icon={<Brain className="w-4 h-4" />} />
                <QuickFact label="Full Stack Development" icon={<Code className="w-4 h-4" />} />
                <QuickFact label="Data Science" icon={<Database className="w-4 h-4" />} />
          
                <QuickFact label="Natural Language Processing" icon={<Terminal className="w-4 h-4" />} />
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skills.map((skill, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10, scale: 1.02 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 200
              }}
              className="group relative cursor-pointer"
            >
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-white/5 to-gray-400/5 rounded-xl blur-md"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
              <div className="relative p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-300 hover:border-white/20 h-full">
                <div className="flex flex-col items-center text-center space-y-4">
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className={`p-4 rounded-lg bg-gradient-to-br ${skill.gradient} text-black relative overflow-hidden`}
                  >
                    <div className="relative z-10">
                      {skill.icon}
                    </div>
                    <motion.div 
                      className="absolute inset-0 bg-white/30"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.6 }}
                    />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white">{skill.title}</h3>
                  <p className="text-gray-400">{skill.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

const QuickFact = ({ label, icon }) => (
  <motion.li
    className="flex justify-between items-center p-3 rounded-lg bg-white/5 backdrop-blur-sm transition-all duration-300"
    whileHover={{ 
      x: 5, 
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      scale: 1.02
    }}
    transition={{ duration: 0.2 }}
  >
    <span className="text-gray-300 flex items-center gap-3">
      <span className="text-gray-500">
        {icon}
      </span>
      {label}
    </span>
    <motion.div 
      className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10"
      whileHover={{ rotate: 90 }}
      transition={{ duration: 0.3 }}
    >
      <ChevronRight className="w-4 h-4 text-gray-500" />
    </motion.div>
  </motion.li>
);

export default About;